import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["alt", "classes", "extraClasses", "displayPlaceholder", "height", "onError", "onLoad", "placeholder", "resource", "src", "srcSet", "type", "width", "widths", "opacity", "ratio", "padding"];
import React from "react";
import PropTypes, { bool, func, instanceOf, number, oneOfType, shape, string } from "prop-types";
import { useImage } from "@magento/peregrine/lib/talons/Image/useImage";
import PlaceholderImage from "./placeholderImage";
import ResourceImage from "./resourceImage";
import SimpleImage from "./simpleImage";
import { mergeClasses } from "@magento/venia-ui/lib/classify";
import { DEFAULT_WIDTH_TO_HEIGHT_RATIO } from "@magento/venia-ui/lib/util/images";
import defaultClasses from "./image.module.scss";
import { cdnImageUrl } from "@Src_main/utils/generalUtils";

/**
 * The Image component renders a placeholder until the image is loaded.
 *
 * @param {object|string}   props.classes any classes to apply to this component
 * @param {bool}     props.displayPlaceholder whether or not to display a placeholder while the image loads or if it errors on load.
 * @param {number}   props.height the intrinsic height of the image & the height to request for the fallback image for browsers that don't support srcset / sizes.
 * @param {function} props.onError callback for error loading image
 * @param {function} props.onLoad callback for when image loads successfully
 * @param {string}   props.placeholder the placeholder source to display while the image loads or if it errors on load
 * @param {string}   props.resource the Magento path to the image ex: /v/d/vd12-rn_main_2.jpg
 * @param {string}   props.src the source of the image, ready to use in an img element
 * @param {string}   props.type the Magento image type ("image-category" / "image-product"). Used to build the resource URL.
 * @param {number}   props.width the intrinsic width of the image & the width to request for the fallback image for browsers that don't support srcset / sizes.
 * @param {number}   props.ratio is the image width to height ratio. Defaults to `DEFAULT_WIDTH_TO_HEIGHT_RATIO` from `util/images.js`.
 * @param {Map}      props.widths a map of breakpoints to possible widths used to create the img's sizes attribute.
 */
var Image = props => {
  var {
      alt,
      classes: propsClasses,
      extraClasses,
      displayPlaceholder = true,
      height,
      onError,
      onLoad,
      placeholder,
      resource,
      src,
      srcSet,
      type,
      width,
      widths,
      opacity,
      ratio,
      padding
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  var talonProps = useImage({
    onError,
    onLoad,
    width,
    widths,
    height,
    ratio
  });
  var {
    handleError,
    handleImageLoad,
    hasError,
    isLoaded,
    resourceWidth: talonResourceWidth,
    resourceHeight: talonResourceHeight
  } = talonProps;
  var classes = mergeClasses(defaultClasses, propsClasses);
  var containerClass = "".concat(classes.root, " ").concat(classes.container);
  var isLoadedClass = isLoaded ? classes.loaded : classes.notLoaded;
  var imageClass = "".concat(classes.image, " ").concat(isLoadedClass);
  var style = {
    minHeight: height,
    minWidth: width,
    opacity: opacity,
    padding: padding
  };

  // If we have a src, use it directly. If not, assume this is a resource image.

  var actualImage = src ? /*#__PURE__*/React.createElement(SimpleImage, _extends({
    alt: alt,
    className: imageClass,
    handleError: handleError,
    handleLoad: handleImageLoad,
    height: talonResourceHeight,
    src: cdnImageUrl(src),
    srcSet: cdnImageUrl(srcSet),
    width: width,
    style: style
  }, rest)) : /*#__PURE__*/React.createElement(ResourceImage, _extends({
    alt: alt,
    className: imageClass,
    handleError: handleError,
    handleLoad: handleImageLoad,
    height: talonResourceHeight,
    resource: resource,
    type: type,
    width: talonResourceWidth,
    widths: widths,
    style: style,
    ratio: ratio
  }, rest));
  return /*#__PURE__*/React.createElement("div", {
    className: "image-container ".concat(containerClass),
    style: opacity > 0 ? {
      filter: "opacity(".concat(opacity, ")")
    } : {}
  }, displayPlaceholder ? /*#__PURE__*/React.createElement(PlaceholderImage, _extends({
    alt: alt,
    classes: classes,
    displayPlaceholder: displayPlaceholder,
    height: height,
    imageHasError: hasError,
    imageIsLoaded: isLoaded,
    src: placeholder,
    width: talonResourceWidth
  }, rest)) : null, actualImage);
};
export default Image;